import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { TenantApi } from "@/openapi/generated";
import type { GetPayJunctionResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUsePayJunctionOptionsGetProps {
  tenantId: number;
}

export const usePayJunctionOptionsGet = (
  { tenantId }: IUsePayJunctionOptionsGetProps,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending, refetch, error } = useQuery<
    GetPayJunctionResponse | null
  >({
    queryKey: [QueryKeys.PayJunctionOptions, tenantId],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(TenantApi, accessToken);

      try {
        // API returns GetPayJunctionResponse directly, not wrapped in ResultOf
        const result = await service.apiAccountTenantIdPayjunctionOptionsGet({
          tenantId,
          xTenantID: tenantId,
        });

        // Check if result is a ResultOf wrapper (has isSuccess property)
        if (result && typeof result === 'object' && 'isSuccess' in result) {
          // Backend returns ResultOfGetPayJunctionResponse wrapper
          const wrappedResult = result as any;
          
          if (!wrappedResult.isSuccess) {
            throw new Error(
              wrappedResult.error?.message || "Failed to load PayJunction options",
            );
          }

          // Return the data from the wrapper
          return wrappedResult.data ?? null;
        }

        // Backend returns GetPayJunctionResponse directly
        // Check if it's a valid response object
        if (result && typeof result === 'object') {
          return result as GetPayJunctionResponse;
        }

        return null;
      } catch (err) {
        // Re-throw HTTP errors (400, 500, etc.) so React Query can handle them
        throw err;
      }
    },
    enabled: options?.enabled !== false && !!accessToken && !!tenantId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    data: data ?? null,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    error,
  };
};
